// InstrumentSampler
// Generic lazy-loading sampler for any pitched instrument.
// Pass a basePath pointing to a folder of per-note MP3 files named like A4.mp3.
// Decodes via Web Audio API (off main thread). Caches decoded AudioBuffers
// so every play after the first is instant.

// Enharmonic equivalents — maps sharps↔flats so e.g. F#4 resolves to Gb4.mp3
// and vice versa when the primary filename isn't found.
const ENHARMONICS = {
  'C#': 'Db', 'D#': 'Eb', 'F#': 'Gb', 'G#': 'Ab', 'A#': 'Bb',
  'Db': 'C#', 'Eb': 'D#', 'Gb': 'F#', 'Ab': 'G#', 'Bb': 'A#',
};

function enharmonic(note) {
  const m = note.match(/^([A-G][#b]?)(\d)$/);
  if (!m) return null;
  const alt = ENHARMONICS[m[1]];
  return alt ? alt + m[2] : null;
}

export class InstrumentSampler {
  constructor({ basePath, extension = 'mp3', polyphonic = false }) {
    this.basePath       = basePath.replace(/\/$/, ''); // strip trailing slash
    this.extension      = extension;
    this.polyphonic     = polyphonic; // true = don't stop other notes on playNote()
    this._ctx           = null;
    this._cache         = new Map(); // note → AudioBuffer
    this._inflight      = new Map(); // note → Promise<AudioBuffer>
    this._activeSources = new Map(); // note → { source, gain } currently playing
  }

  // Lazily create AudioContext — must happen after a user gesture on iOS/Safari.
  _getCtx() {
    if (!this._ctx) {
      this._ctx = new (window.AudioContext || window.webkitAudioContext)();
    }
    return this._ctx;
  }

  // Fetch and decode a note, caching the result. Dedupes concurrent requests.
  // Sharp notes (e.g. F#4) use 's' filenames (Fs4.mp3) to avoid '#' being
  // interpreted as a URL fragment by the browser, which would silently truncate
  // the path and return an HTML page instead of the audio file.
  _load(note) {
    if (this._cache.has(note))    return Promise.resolve(this._cache.get(note));
    if (this._inflight.has(note)) return this._inflight.get(note);

    const toFilename = (n) => n.replace('#', 's'); // F#4 → Fs4, C#4 → Cs4, etc.

    const tryFetch = (filename) => {
      const url = `${this.basePath}/${filename}.${this.extension}`;
      return fetch(url).then(r => {
        if (!r.ok) throw new Error(`[${this.basePath}] Missing sample: ${filename}.${this.extension}`);
        return r.arrayBuffer();
      });
    };

    const promise = tryFetch(toFilename(note))
      .then(buf => this._getCtx().decodeAudioData(buf)) // async, off main thread
      .then(decoded => {
        this._cache.set(note, decoded);
        this._inflight.delete(note);
        return decoded;
      })
      .catch(err => {
        // Always clear inflight on any failure so the next call retries
        // fresh rather than reusing a rejected promise.
        this._inflight.delete(note);
        throw err;
      });

    this._inflight.set(note, promise);
    return promise;
  }

  // Play a note. Returns the BufferSourceNode so the caller can stop() it early.
  // If the same note is already playing, fades it out in 12ms then re-attacks
  // so consecutive identical notes (e.g. D D, B B) sound distinct rather than
  // blurring into a single sustained tone.
  async playNote(note, { volume = 1.0, detune = 0 } = {}) {
    const ctx = this._getCtx();

    let buffer;
    try {
      buffer = await this._load(note);
    } catch (err) {
      console.warn(err.message);
      return null;
    }

    const now = ctx.currentTime;
    const targetVol = Math.max(0, Math.min(2, volume));

    const activeCount = this._activeSources.size;
    console.log(
      '[ViolinSampler] playNote ' + note +
      ' | wall=' + performance.now().toFixed(1) + 'ms' +
      ' | ctx=' + (now * 1000).toFixed(1) + 'ms' +
      ' | activeSources=' + activeCount +
      ' | cacheSize=' + this._cache.size
    );

    // Monophonic instruments (violin): stop ALL active notes before the new one.
    // Polyphonic instruments (piano): skip this — chord tones ring simultaneously.
    //
    // Timeline for monophonic:  all active fade 0–5ms → silence 5–20ms → new attack 20–30ms
    const GAP = this.polyphonic ? 0 : 0.020;
    if (!this.polyphonic && this._activeSources.size > 0) {
      if (activeCount > 0) {
        const stopped = [];
        this._activeSources.forEach((prev, key) => {
          stopped.push(key);
          try {
            prev.gain.gain.cancelScheduledValues(now);
            prev.gain.gain.setValueAtTime(prev.gain.gain.value, now);
            prev.gain.gain.linearRampToValueAtTime(0, now + 0.005);
            prev.source.stop(now + 0.005);
          } catch (_) { /* already stopped — ignore */ }
        });
        console.log('[ViolinSampler]   → stopped all active: [' + stopped.join(', ') + ']');
        this._activeSources.clear();
      }
    }

    const startAt = now + GAP; // new note starts after the silence gap

    const source = ctx.createBufferSource();
    source.buffer       = buffer;
    source.detune.value = detune; // cents — for pitch shifting if needed

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, startAt);
    gain.gain.linearRampToValueAtTime(targetVol, startAt + 0.010); // 10ms attack

    source.connect(gain);
    gain.connect(ctx.destination);
    source.start(startAt);

    this._activeSources.set(note, { source, gain });

    // Remove from active tracking once the sample finishes naturally.
    source.onended = () => {
      if (this._activeSources.get(note)?.source === source) {
        this._activeSources.delete(note);
      }
    };

    return source;
  }

  // Warm the cache for a list of notes. Fetches all in parallel and logs
  // a confirmation once every note is decoded and ready to play instantly.
  preload(notes) {
    const unique = [...new Set(notes)];
    const promises = unique.map(n => this._load(n).catch(() => {}));
    Promise.all(promises).then(() => {
      const cached = unique.filter(n => this._cache.has(n));
      const missing = unique.filter(n => !this._cache.has(n));
      console.log(
        '[ViolinSampler] preload complete — ' + cached.length + '/' + unique.length + ' notes cached: ' + cached.join(' ') +
        (missing.length ? ' | MISSING: ' + missing.join(' ') : '')
      );
    });
  }

  // iOS and Safari suspend AudioContext until a user gesture.
  // Call resume() inside any tap/click handler before playing.
  resume() {
    if (this._ctx && this._ctx.state === 'suspended') this._ctx.resume();
  }

  // How many notes are currently decoded and cached.
  get cacheSize() {
    return this._cache.size;
  }
}

// ── Default instances ────────────────────────────────────────────────────────

export const violinSampler = new InstrumentSampler({
  basePath: '/samples/violin-notes',
});

export const pianoSampler = new InstrumentSampler({
  basePath: '/samples/piano-notes',
  polyphonic: true,  // chord tones must ring simultaneously
});

// Future instruments — uncomment and add their sample folders when ready:
// export const mandolinSampler = new InstrumentSampler({ basePath: '/samples/mandolin-notes' });
// export const guitarSampler   = new InstrumentSampler({ basePath: '/samples/guitar-notes' });
